export const TRACK_TABLE_ALIAS = 'cp_track';

// ── Saved views ───────────────────────────────────────────────────────────────
// The three stage tabs are client-side constants, not rows in custom_views; anything
// else in tabsData is a saved view (marked isIconShow). Ported from WAL radar.const.ts
// RADAR_DEFAULT_TAB_VALUES / RADAR_DEFAULT_TAB_WITH_CREATE_VIEW_NUMBER.
export const TRACK_DEFAULT_TAB_VALUES: string[] = ['dispatched', 'in-transit', 'delivered'];
// 3 stage tabs + the portal backend's cap of 8 saved views per user per tableAlias
// (createView.controller.ts) — the "+" create button hides once this is reached.
export const MAX_TRACK_VIEW_COUNT = 11;

export const TRACK_SORTABLE_INITIAL_STATE = {
    isOpen: false,
    data: [] as any[],
};

// The six schedule columns are the same set WAL Radar shows (RADAR_DEFAULT_COLUMNS). They replaced
// the portal-only combined `date` column (tab-renamed Pick Up / Location Updated / Est. Delivery);
// a saved `date` entry from before that change is dropped by transformTrackColumnsToHeadCells.
export const TRACK_DEFAULT_COLUMNS = [
    { id: 'orderId', name: 'Order ID', visible: true, displayOrder: 1 },
    { id: 'carrierName', name: 'Carrier', visible: true, displayOrder: 2 },
    { id: 'orderType', name: 'Type', visible: true, displayOrder: 3 },
    { id: 'orderStatus', name: 'Status', visible: true, displayOrder: 4 },
    { id: 'pickupDate', name: 'Pickup Date', visible: true, displayOrder: 5 },
    { id: 'pickupType', name: 'Pickup Type', visible: true, displayOrder: 6 },
    { id: 'pickupTime', name: 'Pickup Time', visible: true, displayOrder: 7 },
    { id: 'deliveryDate', name: 'Delivery Date', visible: true, displayOrder: 8 },
    { id: 'deliveryType', name: 'Delivery Type', visible: true, displayOrder: 9 },
    { id: 'deliveryTime', name: 'Delivery Time', visible: true, displayOrder: 10 },
    { id: 'shipperCity', name: 'From', visible: true, displayOrder: 11 },
    { id: 'consigneeCity', name: 'To', visible: true, displayOrder: 12 },
];

// Columns the list API can order by (ids must exist in listRadarOrders' sort maps). Type/Time
// columns are derived display values with no single sort column — same as WAL RADAR_SORTABLE_COLUMNS.
export const TRACK_SORTABLE_COLUMNS = new Set(['orderId', 'carrierName', 'orderType', 'orderStatus', 'pickupDate', 'deliveryDate', 'shipperCity', 'consigneeCity']);

// Widths mirror wal-frontend-v2's RADAR_COLUMN_CLASSNAME_MAP.
export const TRACK_COLUMN_CLASSNAME_MAP: Record<string, string> = {
    orderId: 'max-w-[170px] min-w-[170px]',
    carrierName: 'min-w-[175px] max-w-[175px]',
    orderType: 'min-w-[130px] max-w-[130px]',
    orderStatus: 'min-w-[140px] max-w-[140px]',
    pickupDate: 'min-w-[150px] max-w-[150px] w-[150px]',
    pickupType: 'min-w-[130px] max-w-[130px] w-[130px]',
    pickupTime: 'min-w-[120px] max-w-[120px] w-[120px]',
    deliveryDate: 'min-w-[150px] max-w-[150px] w-[150px]',
    deliveryType: 'min-w-[130px] max-w-[130px] w-[130px]',
    deliveryTime: 'min-w-[120px] max-w-[120px] w-[120px]',
    shipperCity: 'min-w-[185px] max-w-[185px]',
    consigneeCity: 'min-w-[185px] max-w-[185px]',
};

// Carrier-specific tracking-id display name(s) by carrier slug — mirrors the CARRIERS
// constant in wal-frontend-v2 (trackingIdNames only). Prefixes the tracking id in the
// carrier details tooltip, e.g. "PRO #: 532861560".
export const TRACK_CARRIER_TRACKING_ID_NAMES: Record<string, string[]> = {
    ARC_BEST: ['Pickup Confirmation #'],
    POLARIS: ['PRO Bill Number #'],
    DAY_ROSS: ['Shipment Number'],
    FREIGHTCOM: ['Shipment ID'],
    MARITIME_ONTARIO: ['Freight Bill Number'],
    CSA: ['Order ID'],
    KINDERSLEY: ['PRO Bill Number'],
    DAY_ROSS_USA: ['PRO Bill Number'],
    DAY_ROSS_CANADA: ['Shipment Number'],
    AMERICAN_GROUP: ['Shipment ID'],
    TFORCE: ['PRO #'],
    TFORCE_CANADA: ['PRO Bill Number'],
    TST: ['PRO #'],
    XPO: ['PRO #'],
    MANITOULIN: ['ProBill Number'],
    MINIMAX: ['Shipment #'],
    PRIMO: ['BOL #'],
};

export const TRACK_COLUMN_TRANSLATION_MAP: Record<string, string> = {
    orderId: 'Common.Order_ID',
    carrierName: 'Radar.Carrier',
    orderType: 'Radar.Type',
    orderStatus: 'Radar.Status',
    pickupDate: 'Radar.Pickup_Date',
    pickupType: 'Radar.Pickup_Type',
    pickupTime: 'Radar.Pickup_Time',
    deliveryDate: 'Radar.Delivery_Date',
    deliveryType: 'Radar.Delivery_Type',
    deliveryTime: 'Radar.Delivery_Time',
    shipperCity: 'Radar.From',
    consigneeCity: 'Radar.To',
};

// Selectable Mapbox base styles for the Radar map's theme picker. `value` is the
// style URL passed to map.setStyle(); `labelKey` is an i18n key under `Radar`.
// The first entry is the default the persistent map boots with — keep it in sync
// with TRACK_DEFAULT_MAP_STYLE and TrackMapLayout's initial style.
// `as const` matters: this app's i18n is strictly typed, so `t()` rejects a
// `Radar.${string}` template built from a widened `string`. Literal labelKeys make
// the composed key resolve to 'Radar.Light' | 'Radar.Streets' | … which typechecks.
export const TRACK_MAP_THEMES = [
    { labelKey: 'Light', value: 'mapbox://styles/mapbox/light-v11' },
    { labelKey: 'Streets', value: 'mapbox://styles/mapbox/streets-v12' },
    { labelKey: 'Satellite', value: 'mapbox://styles/mapbox/satellite-streets-v12' },
    { labelKey: 'Outdoors', value: 'mapbox://styles/mapbox/outdoors-v12' },
] as const;

// The style URL the persistent map (TrackMapLayout) is created with.
export const TRACK_DEFAULT_MAP_STYLE = 'mapbox://styles/mapbox/light-v11';

// Quick date-range presets for the Track date control — ported 1:1 from WAL radar's
// RADAR_DATE_PRESET (wal-frontend-v2 src/app/Radar/radar.const.ts). Values are stable
// identifiers; the ACTIVE preset is always derived from trackParams start/end dates,
// never stored (see getActiveTrackDatePreset in track.helpers.ts).
export const TRACK_DATE_PRESET = {
    today: 'today',
    tomorrow: 'tomorrow',
    thisWeek: 'this_week',
    thisMonth: 'this_month',
    custom: 'custom',
} as const;

export type ITrackDatePreset = (typeof TRACK_DATE_PRESET)[keyof typeof TRACK_DATE_PRESET];

export const TRACK_DATE_PRESET_OPTIONS = [
    { value: TRACK_DATE_PRESET.today, labelKey: 'Common.Today' },
    { value: TRACK_DATE_PRESET.tomorrow, labelKey: 'Common.Tomorrow' },
    // Radar-scoped keys, matching WAL ("This Week"/"This Month" capitalization).
    { value: TRACK_DATE_PRESET.thisWeek, labelKey: 'Radar.This_Week' },
    { value: TRACK_DATE_PRESET.thisMonth, labelKey: 'Radar.This_Month' },
    { value: TRACK_DATE_PRESET.custom, labelKey: 'Radar.Custom_Range' },
];

// ── Table View filter bar ─────────────────────────────────────────────────────
export const TRACK_FILTER_FIELD_CLASS = 'flex-none min-w-[150px] max-w-[160px]';
export const TRACK_FILTER_SEARCH_FIELD_CLASS = 'flex-none min-w-[150px] max-w-[180px]';
